import {
  getVoipOption,
  getVoipOptionServicePack,
  isIncludeVoipOptionServicePack,
} from '@/logics/option/voip';
import { VoipOptionPriceResponse } from '@/types/api/mypage/voipoptionprice';
import { Constants } from '@/utils/constants';
import { VoipOptionUseStatus } from '@/utils/constants/voip';

/**
 * 「発信者番号通知サービス」の「変更前」のラベル表示用コンポーネントのパラメータ定義。
 */
type NumberNoticeBeforeLabelProps = {
  /** VoIPオプション利用状況取得のレスポンス */
  voipOptionPrice: VoipOptionPriceResponse;
  /** スタイル情報 */
  style: {
    /** testId */
    dataTestId: string;
  };
};

/**
 * 「発信者番号通知サービス」の「変更前」のラベル表示用コンポーネント
 * @param param0 入力情報
 * @returns コンポーネント
 */
export const VoipChangeNumberNoticeBeforeLabel = ({
  voipOptionPrice,
  style,
}: NumberNoticeBeforeLabelProps) => {
  // 現在契約状態の表示に専念し、存在しない場合は未申込に正規化する。
  const option = getVoipOption(
    Constants.VoipOptionClassType.VOIP_SENDER_NUMBER_INFO,
    voipOptionPrice
  );
  if (!option) {
    return <span data-testid={style.dataTestId}>未申込</span>;
  }
  return (
    <span data-testid={style.dataTestId}>
      {option?.use_status === VoipOptionUseStatus.SERVICE ? '利用中' : '未申込'}
    </span>
  );
};

/**
 * 「発信者番号通知サービス」の「変更後」のラベル表示用コンポーネントのパラメータ定義。
 */
type NumberNoticeAfterLabelProps = {
  /** VoIPオプション利用状況取得のレスポンス */
  voipOptionPrice: VoipOptionPriceResponse;
  /** 入力情報 */
  // eslint-disable-next-line react/require-default-props
  changeInput?: boolean | null;
  /** スタイル情報 */
  style: {
    /** testId */
    dataTestId: string;
  };
};

/**
 * 「発信者番号通知サービス」の「変更後」のラベル表示用コンポーネント
 * @param param0 入力情報
 * @returns コンポーネント
 */
export const VoipChangeNumberNoticeAfterLabel = ({
  voipOptionPrice,
  changeInput = null,
  style,
}: NumberNoticeAfterLabelProps) => {
  // 変更入力があるかどうかで、現在状態から遷移後文言へ変換する。
  const option = getVoipOption(
    Constants.VoipOptionClassType.VOIP_SENDER_NUMBER_INFO,
    voipOptionPrice
  );
  if (!option) {
    return <span data-testid={style.dataTestId}>利用しない</span>;
  }
  // 「入力情報 == null」の場合は「ステータス」のみを使用して表示する。
  if (changeInput === null) {
    if (option?.use_status === VoipOptionUseStatus.SERVICE) {
      return <span data-testid={style.dataTestId}>利用しない</span>;
    }
    return <span data-testid={style.dataTestId}>利用する</span>;
  }
  // 「ステータス == 利用中」、「入力あり」の場合は「利用しない」を表示する。
  // 「ステータス != 利用中」、「入力なし」の場合は「利用しない」を表示する。
  if (option?.use_status === VoipOptionUseStatus.SERVICE) {
    if (changeInput) {
      return <span data-testid={style.dataTestId}>利用しない</span>;
    }
    return <span data-testid={style.dataTestId}>利用する</span>;
  }
  if (changeInput) {
    return <span data-testid={style.dataTestId}>利用する</span>;
  }
  return <span data-testid={style.dataTestId}>利用しない</span>;
};

/**
 * 「単品オプション」の「変更前」のラベル表示用コンポーネントのパラメータ定義。
 */
type OptionBeforeLabelProps = {
  /** オプションクラス */
  classType: string;
  /** サービスパック種別 */
  servicePackType: string;
  /** VoIPオプション利用状況取得のレスポンス */
  voipOptionPrice: VoipOptionPriceResponse;
  /** スタイル情報 */
  style: {
    /** testId */
    dataTestId: string;
  };
};

/**
 * 「単品オプション」の「変更前」のラベル表示用コンポーネント
 * @param param0 入力情報
 * @returns コンポーネント
 */
export const VoipChangeOptionBeforeLabel = ({
  classType,
  servicePackType,
  voipOptionPrice,
  style,
}: OptionBeforeLabelProps) => {
  // サービスパック内包オプションは単品状態ではなくパック名で見せる必要がある。
  const isIncludeServicePack = isIncludeVoipOptionServicePack(classType, servicePackType);
  if (isIncludeServicePack) {
    const servicePackName = getVoipOptionServicePack(servicePackType, voipOptionPrice)?.option_name;
    return <span data-testid={style.dataTestId}>{servicePackName}</span>;
  }
  // 単品契約の場合だけ個別オプション状態を参照する。
  const option = getVoipOption(classType, voipOptionPrice);
  if (!option) {
    return <span data-testid={style.dataTestId}>未申込</span>;
  }
  const useStatus = option.use_status;
  if (useStatus === VoipOptionUseStatus.SERVICE) {
    return <span data-testid={style.dataTestId}>利用中</span>;
  }
  return <span data-testid={style.dataTestId}>未申込</span>;
};

/**
 * 「単品オプション」の「変更後」のラベル表示用コンポーネントのパラメータ定義。
 */
type OptionAfterLabelProps = {
  /** オプションクラス */
  classType: string;
  /** サービスパック種別 */
  servicePackType: string;
  /** VoIPオプション利用状況取得のレスポンス */
  voipOptionPrice: VoipOptionPriceResponse;
  /** 入力情報 */
  // eslint-disable-next-line react/require-default-props
  changeInput?: boolean | null;
  /** スタイル情報 */
  style: {
    /** testId */
    dataTestId: string;
  };
};

/**
 * 「付加サービス（単品）」の「変更後」のラベル表示用コンポーネント。
 * @param classType オプション種別
 * @param servicePackType 入力したサービスパック種別の値
 * @param voipOptionPrice VoIPオプション利用状況取得のレスポンス
 * @param style 画面定義情報
 * @returns コンポーネント
 */
export const VoipChangeOptionAfterLabel = ({
  classType,
  servicePackType,
  changeInput = null,
  voipOptionPrice,
  style,
}: OptionAfterLabelProps) => {
  // パック内包時は変更後もパック名表示を優先し、単品の申込/解約文言にしない。
  const isIncludeServicePack = isIncludeVoipOptionServicePack(classType, servicePackType);
  if (isIncludeServicePack) {
    const servicePackName = getVoipOptionServicePack(servicePackType, voipOptionPrice)?.option_name;
    return <span data-testid={style.dataTestId}>{servicePackName}</span>;
  }

  // 単品契約時だけ現在状態と入力状態から変更後文言を導出する。
  const option = getVoipOption(classType, voipOptionPrice);
  if (!option) {
    return <span data-testid={style.dataTestId}>未申込</span>;
  }
  // 「入力情報 == null」の場合は「ステータス」のみを使用して表示する。
  if (changeInput === null) {
    if (option?.use_status === VoipOptionUseStatus.SERVICE) {
      return <span data-testid={style.dataTestId}>解約</span>;
    }
    return <span data-testid={style.dataTestId}>申込む</span>;
  }

  // 「ステータス == 利用中」、「入力あり」の場合は「利用しない」を表示する。
  // 「ステータス != 利用中」、「入力なし」の場合は「利用しない」を表示する。
  if (option.use_status === VoipOptionUseStatus.SERVICE) {
    if (changeInput) {
      return <span data-testid={style.dataTestId}>未申込</span>;
    }
    return <span data-testid={style.dataTestId}>利用する</span>;
  }
  if (changeInput) {
    return <span data-testid={style.dataTestId}>利用する</span>;
  }
  return <span data-testid={style.dataTestId}>未申込</span>;
};
